import React, { useState, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  InputNumber,
  Radio,
  Switch,
  Segmented,
  Spin,
  Empty,
  App,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  SwapOutlined,
  UploadOutlined,
  FallOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useGetInventoryCategoriesQuery,
  useCreateInventoryCategoryMutation,
  useUpdateInventoryCategoryMutation,
  useDeleteInventoryCategoryMutation,
  useGetInventoryItemsQuery,
  useCreateInventoryItemMutation,
  useUpdateInventoryItemMutation,
  useDeleteInventoryItemMutation,
  useAdjustStockMutation,
  useImportInventoryItemsMutation,
} from '../../services/inventoryApi';
import {
  InventoryCategory,
  InventoryItem,
  CreateInventoryCategoryRequest,
  UpdateInventoryCategoryRequest,
  CreateInventoryItemRequest,
  UpdateInventoryItemRequest,
  AdjustStockRequest,
} from '../../types/inventory';
import { useResponsive } from '../../hooks/useResponsive';
import CsvImportModal from '../common/CsvImportModal';
import WasteRegisterModal from './WasteRegisterModal';
import type { ColumnConfig } from '../../types/import';
import { areUnitsCompatible, convertToStockUnit, formatStock } from '../../utils/unitHelpers';
import { formatUnitCost } from '../../utils/currencyHelpers';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

type ActiveView = 'categories' | 'items';

const UNIT_OPTIONS = [
  { label: 'Kilogramos (kg)', value: 'kg' },
  { label: 'Litros (L)', value: 'L' },
  { label: 'Piezas (pz)', value: 'pz' },
  { label: 'Cajas', value: 'caja' },
  { label: 'Gramos (g)', value: 'g' },
  { label: 'Mililitros (ml)', value: 'ml' },
];

const InventoryItemsTab: React.FC = () => {
  const { isMobile, isTablet } = useResponsive();
  const { message: messageApi, modal } = App.useApp();

  // View toggle
  const [activeView, setActiveView] = useState<ActiveView>('items');

  // Category modal state
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<InventoryCategory | null>(null);
  const [categoryForm] = Form.useForm();

  // Item modal state
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [itemForm] = Form.useForm();

  // Stock adjustment modal state
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState<InventoryItem | null>(null);
  const [stockForm] = Form.useForm();

  // Waste (merma) modal state
  const [wasteModalOpen, setWasteModalOpen] = useState(false);
  const [wasteInitialItemId, setWasteInitialItemId] = useState<string | undefined>(undefined);

  // Items filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [activeFilter, setActiveFilter] = useState<string | undefined>(undefined);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // API hooks - Categories
  const {
    data: categories = [],
    isLoading: loadingCategories,
    refetch: refetchCategories,
  } = useGetInventoryCategoriesQuery();
  const [createCategory, { isLoading: creatingCategory }] = useCreateInventoryCategoryMutation();
  const [updateCategory, { isLoading: updatingCategory }] = useUpdateInventoryCategoryMutation();
  const [deleteCategory] = useDeleteInventoryCategoryMutation();

  // API hooks - Items
  const {
    data: allItems = [],
    isLoading: loadingItems,
    refetch: refetchItems,
  } = useGetInventoryItemsQuery();
  // Las preparaciones no llevan stock: viven en su pestaña. Sólo la merma las necesita,
  // porque tirar salsa sí se registra.
  const items = useMemo(() => allItems.filter((item) => !item.isPreparation), [allItems]);
  const [createItem, { isLoading: creatingItem }] = useCreateInventoryItemMutation();
  const [updateItem, { isLoading: updatingItem }] = useUpdateInventoryItemMutation();
  const [deleteItem] = useDeleteInventoryItemMutation();
  const [adjustStock, { isLoading: adjustingStock }] = useAdjustStockMutation();
  const [importInventoryItems] = useImportInventoryItemsMutation();

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.categoryName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = !categoryFilter || item.inventoryCategoryId === categoryFilter;

      const matchesActive =
        activeFilter === undefined ||
        activeFilter === '' ||
        (activeFilter === 'true' && item.isActive) ||
        (activeFilter === 'false' && !item.isActive);

      return matchesSearch && matchesCategory && matchesActive;
    });
  }, [items, searchTerm, categoryFilter, activeFilter]);

  // =========================================================
  // Category handlers
  // =========================================================

  const handleOpenCategoryModal = (category?: InventoryCategory) => {
    if (category) {
      setEditingCategory(category);
      categoryForm.setFieldsValue({
        name: category.name,
        description: category.description || '',
        isActive: category.isActive,
      });
    } else {
      setEditingCategory(null);
      categoryForm.resetFields();
      categoryForm.setFieldsValue({ isActive: true });
    }
    setCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setCategoryModalOpen(false);
    setEditingCategory(null);
    categoryForm.resetFields();
  };

  const handleSaveCategory = async () => {
    try {
      const values = await categoryForm.validateFields();
      if (editingCategory) {
        const data: UpdateInventoryCategoryRequest = {
          name: values.name,
          description: values.description || undefined,
          isActive: values.isActive,
        };
        await updateCategory({ id: editingCategory.id, data }).unwrap();
        messageApi.success('Categoria actualizada exitosamente');
      } else {
        const data: CreateInventoryCategoryRequest = {
          name: values.name,
          description: values.description || undefined,
          isActive: values.isActive,
        };
        await createCategory(data).unwrap();
        messageApi.success('Categoria creada exitosamente');
      }
      handleCloseCategoryModal();
    } catch (error: any) {
      if (error?.errorFields) return; // Form validation error
      messageApi.error(error?.data?.message || 'Error al guardar la categoria');
    }
  };

  const handleDeleteCategory = (category: InventoryCategory) => {
    modal.confirm({
      title: 'Eliminar Categoria',
      content: `¿Estas seguro de eliminar la categoria "${category.name}"? Esta accion no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteCategory(category.id).unwrap();
          messageApi.success('Categoria eliminada exitosamente');
        } catch (error: any) {
          messageApi.error(error?.data?.message || 'Error al eliminar la categoria');
        }
      },
    });
  };

  // =========================================================
  // Item handlers
  // =========================================================

  const handleOpenItemModal = (item?: InventoryItem) => {
    if (item) {
      setEditingItem(item);
      itemForm.setFieldsValue({
        name: item.name,
        sku: item.sku || '',
        description: item.description || '',
        unit: item.unit,
        currentStock: item.currentStock,
        minimumStock: item.minimumStock,
        costPerUnit: item.costPerUnit,
        inventoryCategoryId: item.inventoryCategoryId,
        isActive: item.isActive,
      });
    } else {
      setEditingItem(null);
      itemForm.resetFields();
      itemForm.setFieldsValue({ isActive: true, currentStock: 0, minimumStock: 0, costPerUnit: 0 });
    }
    setItemModalOpen(true);
  };

  const handleCloseItemModal = () => {
    setItemModalOpen(false);
    setEditingItem(null);
    itemForm.resetFields();
  };

  const handleSaveItem = async () => {
    try {
      const values = await itemForm.validateFields();
      if (editingItem) {
        const data: UpdateInventoryItemRequest = {
          name: values.name,
          sku: values.sku || undefined,
          description: values.description || undefined,
          unit: values.unit,
          minimumStock: values.minimumStock ?? 0,
          costPerUnit: values.costPerUnit ?? 0,
          inventoryCategoryId: values.inventoryCategoryId,
          isActive: values.isActive,
          convertValuesToNewUnit: values.convertValuesToNewUnit === true,
        };
        await updateItem({ id: editingItem.id, data }).unwrap();
        messageApi.success('Insumo actualizado exitosamente');
      } else {
        const data: CreateInventoryItemRequest = {
          name: values.name,
          sku: values.sku || undefined,
          description: values.description || undefined,
          unit: values.unit,
          currentStock: values.currentStock ?? 0,
          minimumStock: values.minimumStock ?? 0,
          costPerUnit: values.costPerUnit ?? 0,
          // undefined = el backend la coloca en "General"
          inventoryCategoryId: values.inventoryCategoryId ?? undefined,
          isActive: values.isActive,
        };
        await createItem(data).unwrap();
        messageApi.success('Insumo creado exitosamente');
      }
      handleCloseItemModal();
    } catch (error: any) {
      if (error?.errorFields) return;
      messageApi.error(error?.data?.message || 'Error al guardar el insumo');
    }
  };

  const handleDeleteItem = (item: InventoryItem) => {
    modal.confirm({
      title: 'Eliminar Insumo',
      content: `¿Estas seguro de eliminar el insumo "${item.name}"? Esta accion no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteItem(item.id).unwrap();
          messageApi.success('Insumo eliminado exitosamente');
        } catch (error: any) {
          messageApi.error(error?.data?.message || 'Error al eliminar el insumo');
        }
      },
    });
  };

  // =========================================================
  // Stock adjustment handlers
  // =========================================================

  const handleOpenStockModal = (item: InventoryItem) => {
    setAdjustingItem(item);
    stockForm.resetFields();
    stockForm.setFieldsValue({ quantity: 0, notes: '' });
    setStockModalOpen(true);
  };

  const handleCloseStockModal = () => {
    setStockModalOpen(false);
    setAdjustingItem(null);
    stockForm.resetFields();
  };

  const handleAdjustStock = async () => {
    if (!adjustingItem) return;
    try {
      const values = await stockForm.validateFields();
      const data: AdjustStockRequest = {
        quantity: values.quantity,
        notes: values.notes || undefined,
      };
      await adjustStock({ id: adjustingItem.id, data }).unwrap();
      messageApi.success('Stock ajustado exitosamente');
      handleCloseStockModal();
    } catch (error: any) {
      if (error?.errorFields) return;
      messageApi.error(error?.data?.message || 'Error al ajustar el stock');
    }
  };

  // =========================================================
  // Category columns
  // =========================================================

  const categoryColumns: ColumnsType<InventoryCategory> = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Descripcion',
      dataIndex: 'description',
      key: 'description',
      responsive: ['md'],
      render: (description?: string) => <Text type="secondary">{description || '-'}</Text>,
    },
    {
      title: 'Insumos',
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.itemCount - b.itemCount,
      render: (count: number) => <Text>{count}</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Activa' : 'Inactiva'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenCategoryModal(record)}
          >
            {!isMobile && 'Editar'}
          </Button>
          <Button
            type="default"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteCategory(record)}
          />
        </Space>
      ),
    },
  ];

  // =========================================================
  // Item columns
  // =========================================================

  const itemColumns: ColumnsType<InventoryItem> = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
      responsive: ['lg'],
      render: (sku?: string) => <Text type="secondary">{sku || '-'}</Text>,
    },
    {
      title: 'Categoria',
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: 150,
      responsive: ['md'],
      sorter: (a, b) => a.categoryName.localeCompare(b.categoryName),
    },
    {
      title: 'Stock',
      key: 'stock',
      width: 140,
      sorter: (a, b) => a.currentStock - b.currentStock,
      render: (_, record) => (
        <Tag color={record.isLowStock ? 'red' : 'green'}>
          {formatStock(record.currentStock)} {record.unit}
        </Tag>
      ),
    },
    {
      title: 'Stock Min.',
      dataIndex: 'minimumStock',
      key: 'minimumStock',
      width: 110,
      align: 'right',
      responsive: ['lg'],
      render: (minimumStock: number, record) => (
        <Text type="secondary">
          {minimumStock} {record.unit}
        </Text>
      ),
    },
    {
      title: 'Costo/Unidad',
      dataIndex: 'costPerUnit',
      key: 'costPerUnit',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.costPerUnit - b.costPerUnit,
      render: (cost: number) => <Text>{formatUnitCost(cost)}</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      align: 'center',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenItemModal(record)}
          />
          <Button
            type="primary"
            size="small"
            icon={<SwapOutlined />}
            onClick={() => handleOpenStockModal(record)}
          >
            {!isTablet && 'Ajustar'}
          </Button>
          <Button
            type="default"
            size="small"
            icon={<FallOutlined />}
            title="Registrar merma"
            onClick={() => {
              setWasteInitialItemId(record.id);
              setWasteModalOpen(true);
            }}
          />
          <Button
            type="default"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteItem(record)}
          />
        </Space>
      ),
    },
  ];

  // Tablet-simplified item columns
  const tabletItemColumns: ColumnsType<InventoryItem> = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong style={{ fontSize: 13 }}>{name}</Text>,
    },
    {
      title: 'Stock',
      key: 'stock',
      width: 110,
      render: (_, record) => (
        <Tag color={record.isLowStock ? 'red' : 'green'} style={{ fontSize: 11 }}>
          {formatStock(record.currentStock)} {record.unit}
        </Tag>
      ),
    },
    {
      title: 'Costo',
      dataIndex: 'costPerUnit',
      key: 'costPerUnit',
      width: 90,
      align: 'right',
      render: (cost: number) => <Text style={{ fontSize: 12 }}>{formatUnitCost(cost)}</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenItemModal(record)} />
          <Button size="small" type="primary" icon={<SwapOutlined />} onClick={() => handleOpenStockModal(record)} />
        </Space>
      ),
    },
  ];

  // =========================================================
  // Mobile card for items
  // =========================================================

  const ItemCard: React.FC<{ item: InventoryItem }> = ({ item }) => (
    <Card size="small" style={{ marginBottom: 8 }} hoverable>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text strong style={{ fontSize: 15 }}>{item.name}</Text>
            <Tag color={item.isActive ? 'green' : 'red'} style={{ fontSize: 10 }}>
              {item.isActive ? 'Activo' : 'Inactivo'}
            </Tag>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <Tag color={item.isLowStock ? 'red' : 'green'}>
              {formatStock(item.currentStock)} {item.unit}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>{item.categoryName}</Text>
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
            {item.sku && <span>SKU: {item.sku}</span>}
            <span>Min: {item.minimumStock} {item.unit}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Text strong style={{ fontSize: 15, color: '#1890ff' }}>
            {formatUnitCost(item.costPerUnit)}
          </Text>
          <div style={{ fontSize: 10, color: 'var(--color-neutral-text)' }}>por {item.unit}</div>
        </div>
      </div>
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-color)', display: 'flex', gap: 8 }}>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleOpenItemModal(item)}
          style={{ flex: 1 }}
        >
          Editar
        </Button>
        <Button
          size="small"
          type="primary"
          icon={<SwapOutlined />}
          onClick={() => handleOpenStockModal(item)}
          style={{ flex: 1 }}
        >
          Ajustar Stock
        </Button>
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteItem(item)}
        />
      </div>
    </Card>
  );

  // =========================================================
  // Mobile card for categories
  // =========================================================

  const CategoryCard: React.FC<{ category: InventoryCategory }> = ({ category }) => (
    <Card size="small" style={{ marginBottom: 8 }} hoverable>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text strong style={{ fontSize: 15 }}>{category.name}</Text>
            <Tag color={category.isActive ? 'green' : 'red'} style={{ fontSize: 10 }}>
              {category.isActive ? 'Activa' : 'Inactiva'}
            </Tag>
          </div>
          {category.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>{category.description}</Text>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <Text strong style={{ fontSize: 16 }}>{category.itemCount}</Text>
          <div style={{ fontSize: 10, color: 'var(--color-neutral-text)' }}>insumos</div>
        </div>
      </div>
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-color)', display: 'flex', gap: 8 }}>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleOpenCategoryModal(category)}
          style={{ flex: 1 }}
        >
          Editar
        </Button>
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteCategory(category)}
        />
      </div>
    </Card>
  );

  // =========================================================
  // Render
  // =========================================================

  const isLoading = activeView === 'categories' ? loadingCategories : loadingItems;

  if (isLoading && (activeView === 'categories' ? categories.length === 0 : items.length === 0)) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Cargando {activeView === 'categories' ? 'categorias' : 'insumos'}...</Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* View toggle */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Segmented
          value={activeView}
          onChange={(value) => {
            setActiveView(value as ActiveView);
            setCurrentPage(1);
          }}
          options={[
            { label: 'Insumos', value: 'items' },
            { label: 'Categorias', value: 'categories' },
          ]}
          size={isMobile ? 'middle' : 'large'}
        />
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              if (activeView === 'categories') refetchCategories();
              else refetchItems();
            }}
            size={isMobile ? 'small' : 'middle'}
          >
            {!isMobile && 'Actualizar'}
          </Button>
          {activeView === 'categories' ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenCategoryModal()}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? 'Nueva' : 'Nueva Categoria'}
            </Button>
          ) : (
            <>
              <Button
                icon={<FallOutlined />}
                onClick={() => {
                  setWasteInitialItemId(undefined);
                  setWasteModalOpen(true);
                }}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? 'Merma' : 'Registrar Merma'}
              </Button>
              <Button
                icon={<UploadOutlined />}
                onClick={() => setShowImportModal(true)}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? 'Importar' : 'Importar CSV'}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenItemModal()}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? 'Nuevo' : 'Nuevo Insumo'}
              </Button>
            </>
          )}
        </Space>
      </div>

      {/* Items filters */}
      {activeView === 'items' && (
        <Card styles={{ body: { padding: isMobile ? 12 : 16 } }} style={{ marginBottom: 16 }}>
          {isMobile ? (
            <div>
              <Input
                placeholder="Buscar insumo..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                allowClear
                size="middle"
                style={{ marginBottom: 8 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Select
                  style={{ flex: 1 }}
                  placeholder="Categoria"
                  value={categoryFilter}
                  onChange={(value) => {
                    setCategoryFilter(value || undefined);
                    setCurrentPage(1);
                  }}
                  allowClear
                  size="middle"
                >
                  {categories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                  ))}
                </Select>
                <Select
                  style={{ width: 120 }}
                  placeholder="Estado"
                  value={activeFilter}
                  onChange={(value) => {
                    setActiveFilter(value || undefined);
                    setCurrentPage(1);
                  }}
                  allowClear
                  size="middle"
                >
                  <Option value="true">Activos</Option>
                  <Option value="false">Inactivos</Option>
                </Select>
              </div>
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Buscar por nombre, SKU o categoria"
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Filtrar por categoria"
                  value={categoryFilter}
                  onChange={(value) => {
                    setCategoryFilter(value || undefined);
                    setCurrentPage(1);
                  }}
                  allowClear
                >
                  {categories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Filtrar por estado"
                  value={activeFilter}
                  onChange={(value) => {
                    setActiveFilter(value || undefined);
                    setCurrentPage(1);
                  }}
                  allowClear
                >
                  <Option value="true">Activos</Option>
                  <Option value="false">Inactivos</Option>
                </Select>
              </Col>
            </Row>
          )}
        </Card>
      )}

      {/* Content */}
      <Card styles={{ body: { padding: isMobile ? 12 : 24 } }}>
        {/* Results summary */}
        <div style={{ marginBottom: isMobile ? 8 : 16 }}>
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
            {activeView === 'categories'
              ? `${categories.length} categorias`
              : `${filteredItems.length} de ${items.length} insumos`}
          </Text>
        </div>

        {activeView === 'categories' ? (
          // Categories view
          categories.length === 0 ? (
            <Empty
              description="No hay categorias de inventario"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : isMobile ? (
            <div>
              {categories.map((cat) => (
                <CategoryCard key={cat.id} category={cat} />
              ))}
            </div>
          ) : (
            <Table<InventoryCategory>
              columns={categoryColumns}
              dataSource={categories}
              rowKey="id"
              loading={loadingCategories}
              size={isTablet ? 'small' : 'middle'}
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} categorias`,
              }}
            />
          )
        ) : (
          // Items view
          filteredItems.length === 0 ? (
            <Empty
              description={searchTerm || categoryFilter || activeFilter ? 'No se encontraron insumos' : 'No hay insumos registrados'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : isMobile ? (
            <div>
              {filteredItems
                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                .map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
              {filteredItems.length > pageSize && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                  <Button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Anterior
                  </Button>
                  <span style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>
                    {currentPage} / {Math.ceil(filteredItems.length / pageSize)}
                  </span>
                  <Button
                    disabled={currentPage >= Math.ceil(filteredItems.length / pageSize)}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <Table<InventoryItem>
              columns={isTablet ? tabletItemColumns : itemColumns}
              dataSource={filteredItems}
              rowKey="id"
              loading={loadingItems}
              size={isTablet ? 'small' : 'middle'}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: !isTablet,
                showTotal: isTablet
                  ? undefined
                  : (total, range) => `${range[0]}-${range[1]} de ${total} insumos`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
              }}
              scroll={{ x: isTablet ? 500 : 1100 }}
            />
          )
        )}
      </Card>

      {/* ===== Category Modal ===== */}
      <Modal
        title={editingCategory ? 'Editar Categoria' : 'Nueva Categoria'}
        open={categoryModalOpen}
        onCancel={handleCloseCategoryModal}
        onOk={handleSaveCategory}
        confirmLoading={creatingCategory || updatingCategory}
        okText={editingCategory ? 'Guardar' : 'Crear'}
        cancelText="Cancelar"
        width={isMobile ? '98%' : 500}
        destroyOnClose
      >
        <Form
          form={categoryForm}
          layout="vertical"
          initialValues={{ isActive: true }}
        >
          <Form.Item
            name="name"
            label="Nombre"
            rules={[{ required: true, message: 'El nombre es requerido' }]}
          >
            <Input placeholder="Nombre de la categoria" />
          </Form.Item>

          <Form.Item name="description" label="Descripcion">
            <TextArea rows={3} placeholder="Descripcion de la categoria" />
          </Form.Item>

          <Form.Item name="isActive" label="Activa" valuePropName="checked">
            <Switch checkedChildren="Si" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ===== Item Modal ===== */}
      <Modal
        title={editingItem ? 'Editar Insumo' : 'Nuevo Insumo'}
        open={itemModalOpen}
        onCancel={handleCloseItemModal}
        onOk={handleSaveItem}
        confirmLoading={creatingItem || updatingItem}
        okText={editingItem ? 'Guardar' : 'Crear'}
        cancelText="Cancelar"
        width={isMobile ? '98%' : 700}
        destroyOnClose
      >
        <Form
          form={itemForm}
          layout="vertical"
          initialValues={{ isActive: true, currentStock: 0, minimumStock: 0, costPerUnit: 0 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item
                name="name"
                label="Nombre"
                rules={[{ required: true, message: 'El nombre es requerido' }]}
              >
                <Input placeholder="Nombre del insumo" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="sku" label="SKU">
                <Input placeholder="SKU" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Descripcion">
            <TextArea rows={2} placeholder="Descripcion del insumo" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="unit"
                label="Unidad"
                rules={[{ required: true, message: 'La unidad es requerida' }]}
              >
                <Select placeholder="Seleccionar unidad">
                  {UNIT_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="inventoryCategoryId"
                label="Categoria"
                // Al crear es opcional: sin categoria el backend usa "General"
                rules={editingItem ? [{ required: true, message: 'La categoria es requerida' }] : []}
              >
                <Select
                  placeholder={editingItem ? 'Seleccionar categoria' : 'General (por defecto)'}
                  allowClear={!editingItem}
                >
                  {categories
                    .filter((cat) => cat.isActive)
                    .map((cat) => (
                      <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* De kg a g puede ser "lo cuento en gramos" o "le puse mal la unidad". Sólo quien edita
              lo sabe, y equivocarse multiplica el stock y el costo por mil. */}
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.unit !== next.unit}>
            {({ getFieldValue }) => {
              const newUnit: string | undefined = getFieldValue('unit');
              if (
                !editingItem ||
                !newUnit ||
                newUnit.toLowerCase() === editingItem.unit.toLowerCase() ||
                !areUnitsCompatible(newUnit, editingItem.unit) ||
                (editingItem.currentStock === 0 &&
                  editingItem.minimumStock === 0 &&
                  editingItem.costPerUnit === 0)
              ) {
                return null;
              }
              const factor = convertToStockUnit(1, editingItem.unit, newUnit);
              return (
                <Form.Item
                  name="convertValuesToNewUnit"
                  label={`Pasas de ${editingItem.unit} a ${newUnit}: ¿qué hacemos con los números?`}
                  rules={[{ required: true, message: 'Elige una opción' }]}
                >
                  <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Radio value={true}>
                      Convertirlos: {formatStock(editingItem.currentStock)} {editingItem.unit} son{' '}
                      {formatStock(editingItem.currentStock * factor)} {newUnit}, y el costo queda
                      en {formatUnitCost(editingItem.costPerUnit / factor)} por {newUnit}
                    </Radio>
                    <Radio value={false}>
                      Dejarlos igual: la unidad estaba mal capturada y los números ya son en{' '}
                      {newUnit}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            {!editingItem && (
              <Col xs={24} sm={8}>
                <Form.Item name="currentStock" label="Stock Actual">
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={editingItem ? 12 : 8}>
              <Form.Item name="minimumStock" label="Stock Minimo">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={editingItem ? 12 : 8}>
              {/* Sin precision: redondearía a dos decimales al salir del campo, y $0.025 por g
                  llegaría como $0.03 al costo de cada receta */}
              <Form.Item name="costPerUnit" label="Costo por Unidad">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  prefix="$"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="isActive" label="Activo" valuePropName="checked">
            <Switch checkedChildren="Si" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ===== CSV Import Modal ===== */}
      <CsvImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Importar Insumos"
        templateEndpoint="/inventory/items/import/template"
        templateFileName="plantilla_insumos.csv"
        expectedColumns={[
          { name: 'Nombre', label: 'Nombre', required: true, type: 'string' },
          { name: 'SKU', label: 'SKU', required: false, type: 'string' },
          { name: 'Descripcion', label: 'Descripcion', required: false, type: 'string' },
          { name: 'Unidad', label: 'Unidad', required: true, type: 'string', validate: (v) => ['kg', 'g', 'L', 'l', 'ml', 'pz', 'caja', 'bolsa', 'paquete', 'lata', 'botella', 'sobre', 'pieza', 'unidad'].includes(v) ? null : 'Unidad invalida' },
          { name: 'StockActual', label: 'Stock Actual', required: false, type: 'number' },
          { name: 'StockMinimo', label: 'Stock Minimo', required: false, type: 'number' },
          { name: 'CostoPorUnidad', label: 'Costo por Unidad', required: false, type: 'number' },
          { name: 'Categoria', label: 'Categoria', required: true, type: 'string' },
          { name: 'Activo', label: 'Activo', required: false, type: 'boolean' },
        ] as ColumnConfig[]}
        onImport={async (formData) => {
          const result = await importInventoryItems(formData).unwrap();
          return result;
        }}
        onImportSuccess={() => {
          refetchItems();
          refetchCategories();
        }}
      />

      {/* ===== Stock Adjustment Modal ===== */}
      <Modal
        title={`Ajustar Stock - ${adjustingItem?.name || ''}`}
        open={stockModalOpen}
        onCancel={handleCloseStockModal}
        onOk={handleAdjustStock}
        confirmLoading={adjustingStock}
        okText="Ajustar"
        cancelText="Cancelar"
        width={isMobile ? '98%' : 450}
        destroyOnClose
      >
        {adjustingItem && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: 'var(--bg-secondary)', borderRadius: 8 }}>
            <Text type="secondary">Stock actual: </Text>
            <Tag color={adjustingItem.isLowStock ? 'red' : 'green'}>
              {formatStock(adjustingItem.currentStock)} {adjustingItem.unit}
            </Tag>
            <span style={{ marginLeft: 8 }}>
              <Text type="secondary">Min: {adjustingItem.minimumStock} {adjustingItem.unit}</Text>
            </span>
          </div>
        )}
        <Form
          form={stockForm}
          layout="vertical"
          initialValues={{ quantity: 0, notes: '' }}
        >
          <Form.Item
            name="quantity"
            label="Cantidad (positiva para agregar, negativa para restar)"
            rules={[
              { required: true, message: 'La cantidad es requerida' },
              {
                validator: (_, value) => {
                  if (value === 0) {
                    return Promise.reject('La cantidad no puede ser cero');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Ej: 10 o -5"
            />
          </Form.Item>

          <Form.Item name="notes" label="Notas">
            <TextArea rows={3} placeholder="Motivo del ajuste (opcional)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ===== Waste (merma) Modal ===== */}
      <WasteRegisterModal
        open={wasteModalOpen}
        onClose={() => setWasteModalOpen(false)}
        items={allItems}
        initialItemId={wasteInitialItemId}
      />
    </div>
  );
};

export default InventoryItemsTab;
